import React from 'react';
import { CL_TOKENS, PILL_TONE, SENTIMENT_META } from '../../receptionist-call-logs/config';
import type { CallSentiment, PillMeta } from '../../receptionist-call-logs';
import { Icon } from '../Icon/Icon';

/** Pill renderer driven by a config-meta entry. */
export function ConfigPill({
  meta,
  size = 'normal',
}: {
  meta: PillMeta | undefined;
  size?: 'normal' | 'small';
}) {
  if (!meta) return null;
  const tone = PILL_TONE[meta.tone];
  const small = size === 'small';
  return (
    <span
      className="inline-flex items-center whitespace-nowrap rounded-full font-semibold tracking-[-0.005em]"
      style={{
        background: tone.bg,
        color: tone.color,
        fontSize: small ? 10.5 : 11.5,
        padding: small ? '1px 7px' : '2px 9px',
        lineHeight: 1.5,
      }}
    >
      {meta.label}
    </span>
  );
}

/** Coloured-dot sentiment indicator. */
export function SentimentDot({ sentiment }: { sentiment: CallSentiment }) {
  const cfg = SENTIMENT_META[sentiment];
  return (
    <span
      className="inline-flex items-center gap-1.5 font-medium tracking-[-0.005em]"
      style={{ fontSize: 11.5, color: CL_TOKENS.ink3 }}
    >
      <span className="h-[7px] w-[7px] flex-none rounded-full" style={{ background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

export function getInitials(name: string): string {
  if (!name || name === 'Unknown caller') return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? '';
  if (parts.length === 1) return (first.charAt(0) || '?').toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

/** Round patient avatar (brass-tinted) with initials or a fallback user icon. */
export function PatientAvatar({
  size = 32,
  initials,
}: {
  size?: number;
  initials?: string | null;
}) {
  return (
    <div
      className="inline-flex flex-none items-center justify-center rounded-full font-semibold tracking-[-0.005em]"
      style={{
        width: size,
        height: size,
        background: CL_TOKENS.brassTint,
        color: CL_TOKENS.brassDeep,
        border: `1px solid ${CL_TOKENS.brassBorder}`,
        fontSize: size * 0.34,
      }}
    >
      {initials ? initials : <Icon kind="team" size={Math.round(size * 0.5)} />}
    </div>
  );
}

export function parseDurationToSec(d: string | undefined): number {
  if (!d) return 0;
  const [m, s] = d.split(':').map((x) => parseInt(x, 10) || 0);
  return (m ?? 0) * 60 + (s ?? 0);
}

export function secToTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
